import React, { useState } from 'react';
import TekkriHeader from '../components/TekkriHeader';
import TekkriSidebar from '../components/TekkriSidebar';
import TekkriMetricCard from '../components/TekkriMetricCard';
import TrendChart from '../components/TrendChart';
import FormDialog from '../components/FormDialog';
import AlertConfigForm from '../components/AlertConfigForm';
import { projectMockData, projectHistoricalData } from '../data/projectMockData';
import { useToast } from '../../../hooks/useToast';
import '../tekkr.css';

/**
 * TekkrProjectView Page
 * Project-level adoption monitoring for a single team
 */
const TekkrProjectView: React.FC = () => {
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const { showToast } = useToast();

  const handleConfigureAlert = () => {
    setShowAlertDialog(true);
  };

  const handleSaveAlert = () => {
    setShowAlertDialog(false);
    showToast({
      title: 'Success',
      message: 'Alert configured successfully!',
      type: 'success'
    });
  };

  const handleViewInJira = () => {
    showToast({
      title: 'Info',
      message: 'This would open Jira (prototype only)',
      type: 'info'
    });
  };

  // Define metrics for trend chart
  const trendMetrics = [
    { key: 'channelCompliance', label: 'Slack Channel Compliance', color: '#e91e8c' },
    { key: 'slaAdherence', label: 'SLA Adherence', color: '#7c3aed' },
    { key: 'timeToFirstResponse', label: 'Response Time', color: '#f59e0b' },
    { key: 'overdueBacklog', label: 'Overdue Backlog', color: '#ef4444' }
  ];

  const handlePhaseClick = (phaseId: string) => {
    showToast({
      title: 'Info',
      message: `Phase ${phaseId} is not available in this prototype`,
      type: 'info'
    });
  };

  return (
    <div className="tekkr-container">
      {/* Tekkr Header */}
      <TekkriHeader
        showBreadcrumb={true}
        breadcrumbText="Jira Service Management Rollout Program"
        breadcrumbLink="/tekkr/program-view"
        actions={
          <button
            className="tekkr-btn tekkr-btn-primary"
            onClick={handleConfigureAlert}
          >
            + Configure Alert
          </button>
        }
      />

      {/* Layout with Sidebar */}
      <div className="tekkr-layout">
        {/* Sidebar */}
        <TekkriSidebar onPhaseClick={handlePhaseClick} />

        {/* Main Content */}
        <main className="tekkr-main">
          <div style={{ 
            marginBottom: '1.5rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <h1 style={{ 
              fontSize: '1.875rem', 
              fontWeight: '600', 
              color: '#ffffff',
              margin: 0
            }}>
              {projectMockData.projectName}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                color: '#ef4444'
              }}>
                68%
              </span>
              <span className="tekkr-status-badge red">
                Off track
              </span>
            </div>
          </div>
        {/* Metrics Grid - 4 cards in one row */}
        <div className="tekkr-metrics-grid-compact">
          <TekkriMetricCard
            label="Slack Channel Compliance Rate"
            current={projectMockData.metrics.channelCompliance.current}
            target={projectMockData.metrics.channelCompliance.target}
            unit={projectMockData.metrics.channelCompliance.unit}
            trend={projectMockData.metrics.channelCompliance.trend}
            isHigherBetter={true}
          />
          <TekkriMetricCard
            label="SLA Adherence"
            current={projectMockData.metrics.slaAdherence.current}
            target={projectMockData.metrics.slaAdherence.target}
            unit={projectMockData.metrics.slaAdherence.unit}
            trend={projectMockData.metrics.slaAdherence.trend}
            isHigherBetter={true}
            alertCount={1}
          />
          <TekkriMetricCard
            label="Time to First Response"
            current={projectMockData.metrics.timeToFirstResponse.current}
            target={projectMockData.metrics.timeToFirstResponse.target}
            unit={projectMockData.metrics.timeToFirstResponse.unit}
            trend={projectMockData.metrics.timeToFirstResponse.trend}
            isHigherBetter={false}
          />
          <TekkriMetricCard
            label="Overdue Backlog"
            current={projectMockData.metrics.overdueBacklog.current}
            target={projectMockData.metrics.overdueBacklog.target}
            unit={projectMockData.metrics.overdueBacklog.unit}
            trend={projectMockData.metrics.overdueBacklog.trend}
            isHigherBetter={false}
            quickAction={{
              label: 'View in Jira',
              onClick: handleViewInJira
            }}
          />
        </div>

        {/* Trend Chart */}
        <TrendChart
          data={projectHistoricalData}
          metrics={trendMetrics}
          defaultMetric="channelCompliance"
          title="4-Week Trend Analysis"
          showAllByDefault={true}
        />
        </main>
      </div>

      {/* Alert Configuration Dialog */}
      {showAlertDialog && (
        <FormDialog
          title="Configure Alert"
          onConfirm={handleSaveAlert}
          onCancel={() => setShowAlertDialog(false)}
          confirmText="Save Alert"
        >
          <AlertConfigForm showScopeField={false} />
        </FormDialog>
      )}
    </div>
  );
};

export default TekkrProjectView;

